import { css } from '@emotion/css';
import { Text } from '@volumental/common';
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type FootKeypoints,
  type FeetKeypoints,
  type Point2D,
} from '../../../api/gql/graphql';
import { Checkmark } from './checkmark';
import { ShaderImage } from './shader-image';

const backgroundStyle = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  text-align: center;
`;

const wrapperStyle = css`
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  aspect-ratio: 1;
  position: relative;
  display: inline-flex;
  margin: 24px auto;
`;

const innerWrapperStyle = css`
  position: relative;
`;

const imageStyle = css`
  width: 100%;
  display: block;
`;

const svgStyle = css`
  top: 0;
  left: 0;
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: transparent;
  transition: opacity 0.3s ease-in-out;
`;

enum AnimationState {
  Loading = 'loading',
  PaperDetected = 'paper-detected',
  FeetDetected = 'feet-detected',
  Finalise = 'finalise',
}

const DURATIONS: Record<AnimationState, number> = {
  [AnimationState.Loading]: 0,
  [AnimationState.PaperDetected]: 1600,
  [AnimationState.FeetDetected]: 1600,
  [AnimationState.Finalise]: 3000,
};

const listKeypoints = (keypoints: FootKeypoints): Point2D[] => {
  return [
    keypoints.toe,
    keypoints.heel,
    keypoints.lateralPoint,
    keypoints.medialPoint,
  ];
};

function hasShader(state: AnimationState): boolean {
  return state !== AnimationState.Finalise;
}

function hasPaperKeypoints(state: AnimationState): boolean {
  return state !== AnimationState.Loading;
}

function hasFeetKeypoints(state: AnimationState): boolean {
  switch (state) {
    case AnimationState.Loading:
    case AnimationState.PaperDetected: {
      return false;
    }
    case AnimationState.FeetDetected:
    case AnimationState.Finalise: {
      return true;
    }
  }
}

function hasCheckmark(state: AnimationState, isValidated: boolean): boolean {
  return state === AnimationState.Finalise && isValidated;
}

interface ValidationModalProps {
  blob: Blob;
  isValidated: boolean;
  paperCorners?: Point2D[];
  feetKeypoints?: FeetKeypoints;
  onNext: () => void;
}

export function ValidationModal({
  blob,
  isValidated,
  onNext,
  paperCorners,
  feetKeypoints,
}: ValidationModalProps): JSX.Element {
  const { t } = useTranslation();
  const imageUrl = useMemo(() => URL.createObjectURL(blob), [blob]);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [animationState, setAnimationState] = useState(AnimationState.Loading);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    function clearTimer(): void {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
    }

    function handleNextState(): void {
      switch (animationState) {
        case AnimationState.Loading: {
          if (paperCorners?.length) {
            setAnimationState(AnimationState.PaperDetected);
          }
          break;
        }
        case AnimationState.PaperDetected: {
          setAnimationState(AnimationState.FeetDetected);
          break;
        }
        case AnimationState.FeetDetected: {
          setAnimationState(AnimationState.Finalise);
          break;
        }
        case AnimationState.Finalise: {
          onNext();
          break;
        }
      }
    }
    clearTimer();
    if (animationState === AnimationState.Loading) {
      handleNextState();
    } else {
      timerRef.current = setTimeout(() => {
        handleNextState();
      }, DURATIONS[animationState]);
    }

    return clearTimer;
  }, [animationState, paperCorners, onNext]);

  useEffect(() => {
    const img = new Image();
    img.src = imageUrl;
    img.addEventListener('load', () => {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    });
  }, [imageUrl]);

  return (
    <div className={backgroundStyle}>
      <Text style={{ color: 'black', fontSize: '21px' }}>
        {hasCheckmark(animationState, isValidated)
          ? t('validationModal.done')
          : t('validationModal.progress')}
      </Text>
      <div className={wrapperStyle}>
        <Checkmark
          alt={t('imageAlt.checkMark')}
          show={hasCheckmark(animationState, isValidated)}
          size={50}
        />

        <div className={innerWrapperStyle}>
          <img
            className={imageStyle}
            src={imageUrl}
            alt={t('imageAlt.validate')}
            role="presentation"
          />
          {hasShader(animationState) ? (
            <ShaderImage imageBlob={blob} />
          ) : undefined}
          <svg
            className={svgStyle}
            viewBox={`0 0 ${imageDimensions.width.toString()} ${imageDimensions.height.toString()}`}
            style={{ opacity: paperCorners && feetKeypoints ? 1 : 0 }}
          >
            {paperCorners && hasPaperKeypoints(animationState) ? (
              <>
                <polygon
                  points={paperCorners
                    .map((p) => `${p.x.toString()},${p.y.toString()}`)
                    .join(' ')}
                  stroke="white"
                  strokeWidth={0.006 * imageDimensions.width}
                  fill="none"
                />
                {paperCorners.map((point, index) => (
                  <circle
                    key={`point ${index.toString()}`}
                    cx={point.x}
                    cy={point.y}
                    r={0.014 * imageDimensions.width}
                    fill="#72E08D"
                    stroke="white"
                    strokeWidth={0.005 * imageDimensions.width}
                  />
                ))}
              </>
            ) : undefined}

            {feetKeypoints && hasFeetKeypoints(animationState) ? (
              <>
                {[feetKeypoints.left, feetKeypoints.right]
                  .flatMap((keypoints) => listKeypoints(keypoints))
                  .map((point, index) => (
                    <circle
                      key={`point ${index.toString()}`}
                      cx={point.x}
                      cy={point.y}
                      r={0.014 * imageDimensions.width}
                      fill="#72E08D"
                      stroke="white"
                      strokeWidth={0.005 * imageDimensions.width}
                    />
                  ))}
              </>
            ) : undefined}
          </svg>
        </div>
      </div>
    </div>
  );
}
